import { useState } from '#app'

export interface LabelChatRow {
    labelId: string
    name: string
    logo: string | null
    threadId: string | null
    lastMessageAt: string | null
    lastMessagePreview: string
    lastMessageBy: string | null
    unread: number
}

/**
 * Composable for the label-chat sidebar section.
 *
 * Backed by `GET /api/chat/labels` (list) and `GET /api/chat/labels/:id/thread`
 * (upsert + hydrate the thread). Entering a room resolves a threadId that the
 * existing thread view can render, so the DM/group renderer is reused.
 */
export function useChatLabels() {
    const labels = useState<LabelChatRow[]>('chat-labels', () => [])
    const loading = useState('chat-labels-loading', () => false)
    const loaded = useState('chat-labels-loaded', () => false)
    const entering = useState<string | null>('chat-labels-entering', () => null)

    async function loadLabels() {
        if (loading.value) return
        loading.value = true
        try {
            const res = await $fetch<{ labels: LabelChatRow[] }>('/api/chat/labels')
            labels.value = res.labels || []
            loaded.value = true
        } catch (err) {
            // Chat disabled, not logged in, etc. — keep list empty, don't spam.
            labels.value = []
            loaded.value = true
        } finally {
            loading.value = false
        }
    }

    /**
     * Ensure a label thread exists and return its threadId. Triggers thread
     * creation server-side on first access. Returns null on error (caller
     * decides how to surface).
     */
    async function enterLabel(labelId: string): Promise<string | null> {
        if (entering.value) return null
        entering.value = labelId
        try {
            const res = await $fetch<{ thread: { id: string } }>(`/api/chat/labels/${labelId}/thread`)
            const threadId = res?.thread?.id || null
            if (threadId) {
                // Merge the freshly-resolved threadId into our cached list so
                // a refresh isn't needed.
                const row = labels.value.find(l => l.labelId === labelId)
                if (row && !row.threadId) row.threadId = threadId
            }
            return threadId
        } catch (err: any) {
            console.warn('[chat:labels] enterLabel failed', err?.data?.statusMessage || err)
            return null
        } finally {
            entering.value = null
        }
    }

    /** Mutate an unread counter without a round-trip (for local optimism). */
    function markLabelRead(labelId: string) {
        const row = labels.value.find(l => l.labelId === labelId)
        if (row) row.unread = 0
    }

    return {
        labels,
        loading,
        loaded,
        entering,
        loadLabels,
        enterLabel,
        markLabelRead
    }
}
